import { useCallback, useEffect, useMemo, useState } from 'react';
import usePlacesAutocomplete from 'use-places-autocomplete';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { XCircleIcon, ArrowLeftIcon, MapPinIcon } from '@heroicons/react/24/solid';
import Loader from '../../../components/Loader';
import { GpsIcon } from '../../../utils/svg';
import { GEOLOCATION_PERMISSION, centerLocation } from '../../../utils/constants';
import Footer from '../../../components/Footer';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../../../store/reducers/user';
import { getDetailLocationFromLatLong, isEmptyData, sendAnalyticsPageView } from '../../../utils/helper';

const mapOptions = {
	disableDefaultUI: true,
	zoomControl: false,
	mapTypeControl: false,
	fullscreenControl: false,
	streetViewControl: false,
	rotateControl: false,
	gestureHandling: 'greedy',
};

const CustomGoogleMap = (props) => {
	const { onClose, isFormAddress } = props;
	const dispatch = useDispatch();

	const { coords, permission: permissionGeolocation } = useSelector((store) => store?.gps);
	const [form, setForm] = useState({
		zoom: 4,
		name: null,
		address: null,
		latitude: null,
		longitude: null,
		country: null,
		state: null,
		city: null,
		village: null,
		district: null,
		postalCode: null,
		detail: null,
	});

	const [isLoading, setIsLoading] = useState(true);
	const [isVisibleFooter, setIsVisibleFooter] = useState(false);

	const {
		ready,
		value,
		suggestions: { status, data },
		setValue,
		clearSuggestions,
	} = usePlacesAutocomplete({
		debounce: 500,
		requestOptions: { types: [] },
	});

	useEffect(() => {
		sendAnalyticsPageView('maps_session');
	}, []);

	useEffect(() => {
		if (ready) {
			setIsLoading(false);
		}
	}, [ready]);

	const onLoad = useCallback(() => {
		console.log('onLoad');
		setTimeout(() => {
			const elementsToHide = document.querySelectorAll('.gmnoprint');
			elementsToHide.forEach((el) => (el.style.display = 'none'));
		}, 1000);
	}, []);

	const handleOnClick = async (data) => {
		console.log('handleOnClick');
		const lat = data?.latLng?.lat();
		const lng = data?.latLng?.lng();
		const place_id = data?.placeId;
		const formated = await getDetailLocationFromLatLong(lat, lng, place_id);
		setForm({
			zoom: 15,
			name: formated?.name,
			address: formated?.address,
			latitude: formated?.lat,
			longitude: formated?.lng,
			country: formated?.country,
			state: formated?.state,
			city: formated?.city,
			village: formated?.village,
			district: formated?.district,
			postalCode: formated?.postalCode ?? '',
			detail: formated?.detail,
		});
		setValue(formated?.address);
		setIsVisibleFooter(true);
	};

	const handleDragEnd = async (data = null) => {
		console.log('handleDragEnd');
		const lat = data?.latLng?.lat();
		const lng = data?.latLng?.lng();
		const formated = await getDetailLocationFromLatLong(lat, lng);
		setForm({
			zoom: 15,
			name: formated?.name,
			address: formated?.address,
			latitude: formated?.lat,
			longitude: formated?.lng,
			country: formated?.country,
			state: formated?.state,
			city: formated?.city,
			village: formated?.village,
			district: formated?.district,
			postalCode: formated?.postalCode ?? '',
			detail: formated?.detail,
		});
		setValue(formated?.address);
		setIsVisibleFooter(true);
	};

	const handleInput = (e) => {
		console.log('handleInput: ', e.target.value);
		const value = e.target.value;

		setValue(value, false);
		if (value.trim().length >= 3) {
			setValue(value);
		}
	};

	const handleSelect = async (data) => {
		console.log('handleSelect');
		const place_id = data?.place_id;
		const formated = await getDetailLocationFromLatLong(null, null, place_id);
		setForm({
			zoom: 15,
			name: formated?.name,
			address: formated?.address,
			latitude: formated?.lat,
			longitude: formated?.lng,
			country: formated?.country,
			state: formated?.state,
			city: formated?.city,
			village: formated?.village,
			district: formated?.district,
			postalCode: formated?.postalCode ?? '',
			detail: formated?.detail,
		});
		setValue(formated?.address);
		setIsVisibleFooter(true);
	};

	const handleConfirmLocation = (data = null) => {
		console.log('handleConfirmLocation', data);
		if (isEmptyData(data?.latitude) || isEmptyData(data?.longitude)) return;
		const address = {
			addressName: null,
			address: data?.address,
			latitude: data?.latitude,
			longitude: data?.longitude,
			country: data?.country,
			state: data?.state,
			city: data?.city,
			village: data?.village,
			district: data?.district,
			postalCode: data?.postalCode ?? '',
			detail: data?.detail,
		};
		if (!isFormAddress) {
			dispatch(setUser(address));
		}
		onClose(isFormAddress ? address : null);
	};

	const handleGetCurrentLocation = async () => {
		console.log('handleGetCurrentLocation');
		if (isEmptyData(coords?.latitude) || isEmptyData(coords?.longitude)) return;
		const formated = await getDetailLocationFromLatLong(coords?.latitude, coords?.longitude);
		console.log('formated: ', formated);
		setForm({
			zoom: 15,
			name: formated?.name,
			address: formated?.address,
			latitude: formated?.lat,
			longitude: formated?.lng,
			country: formated?.country,
			state: formated?.state,
			city: formated?.city,
			village: formated?.village,
			district: formated?.district,
			postalCode: formated?.postalCode ?? '',
			detail: formated?.detail,
		});
		setValue(formated?.address);
		setIsVisibleFooter(true);
	};

	const renderSuggestions = () => {
		return data.map((suggestion) => {
			const {
				place_id,
				structured_formatting: { main_text, secondary_text },
			} = suggestion;
			if (!secondary_text) {
				return null;
			}
			return (
				<div key={place_id} onClick={() => handleSelect(suggestion)}>
					<div className='cursor-pointer p-2 border-b-1 text-gray-900 text-xs hover:bg-blue-500 hover:text-white'>{`${main_text} - ${secondary_text}`}</div>
				</div>
			);
		});
	};

	const center = useMemo(() => {
		if (isEmptyData(form?.latitude) || isEmptyData(form?.longitude)) return centerLocation;
		return {
			lat: form?.latitude,
			lng: form?.longitude,
		};
	}, [form?.latitude, form?.longitude]);

	return (
		<>
			<style>
				{`
					[data-rsbs-header] {
						display: none !important;
					}
				`}
			</style>
			{isLoading ? (
				<Loader />
			) : (
				<>
					<div className='h-screen w-full flex justify-center items-center'>
						<div className='relative h-screen w-full'>
							<div className='absolute z-10 mt-3 w-full'>
								<div className='flex mx-4 items-center'>
									<div id='back_btn_maps' className='mr-2 flex justify-center items-center cursor-pointer' onClick={onClose}>
										<ArrowLeftIcon className='h-7 w-7 text-gray-900 bg-white rounded-full p-1.5' />
									</div>
									<div className='relative rounded-full shadow-sm w-full'>
										<input
											id='maps_search'
											type='text'
											className='block w-full border-0 py-1.5 !pl-2.5 !pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-inset focus:ring-0 sm:text-sm sm:leading-6 !m-0 !text-xs !rounded-inherit !h-8'
											placeholder='Mau cari alamat apa?'
											value={value || ''}
											onChange={handleInput}
											disabled={isLoading}
										/>
										{value && (
											<div
												id='maps_cancel_search'
												className='absolute inset-y-0 right-0 flex items-center mr-2 cursor-pointer'
												onClick={() => {
													setValue('');
													setIsVisibleFooter(false);
													setForm({
														zoom: 4,
														name: null,
														address: null,
														lat: null,
														lng: null,
													});
													clearSuggestions();
												}}>
												<XCircleIcon className='text-gray-500 h-4' />
											</div>
										)}
									</div>
								</div>
								{status === 'OK' && !isVisibleFooter && <div className='my-2 mx-4 border-1 rounded-lg bg-white'>{renderSuggestions()}</div>}
							</div>
							<GoogleMap
								mapContainerClassName='h-full w-full'
								clickableIcons
								center={center}
								zoom={form?.zoom}
								onClick={(e) => {
									handleOnClick(e);
								}}
								onLoad={onLoad}
								options={mapOptions}>
								{center && (
									<Marker
										position={center}
										draggable
										onDragEnd={(e) => {
											handleDragEnd(e);
										}}
									/>
								)}
							</GoogleMap>
							<Footer className='!shadow-none'>
								<div className='flex justify-end mb-5 mr-3'>
									<div
										id='maps_current_location'
										className={`bg-white p-2 rounded-full cursor-pointer ${(!permissionGeolocation || permissionGeolocation === GEOLOCATION_PERMISSION.DENIED) && 'hidden'}`}
										onClick={handleGetCurrentLocation}>
										<GpsIcon className={'stroke-blue-500'} />
									</div>
								</div>
								<div className={`bg-white p-4 rounded-t-2xl transition-transform duration-500 ease-in-out  shadow-custom ${isVisibleFooter ? 'translate-y-0 relative' : 'translate-y-full hidden'}`}>
									<div className='flex justify-center'>
										<div className='bg-gray-300 h-1.5 w-9 rounded-full' />
									</div>
									<div className='mt-4 flex justify-between items-center'>
										<div className='text-gray-800 text-sm font-semibold'>Pilih Lokasi</div>
										<button onClick={() => console.log('click edit footer')} className='border-1 border-byzantium py-1.5 px-4 text-base font-semibold text-purple-700 hidden'>
											Ubah
										</button>
									</div>
									<div className='mt-4 flex items-center'>
										<div className='pr-3'>
											<MapPinIcon className='h-4 w-4 text-gray-400' />
										</div>
										<div>
											<div className='text-gray-800 text-base font-semibold'>{form?.name}</div>
											<div className='text-xs text-gray-500'>{form?.address}</div>
										</div>
									</div>
									<button id='maps_confirm_location' className='bg-[var(--rose-violet)] w-full text-white font-normal text-sm py-3 border-0 mt-5' onClick={() => handleConfirmLocation(form)}>
										Konfirmasi Lokasi
									</button>
								</div>
							</Footer>
						</div>
					</div>
				</>
			)}
		</>
	);
};

export default CustomGoogleMap;
